import React, { useEffect } from 'react';
import { Tooltip } from 'antd';
import { useParams } from 'react-router-dom';
import styles from './Pod.module.css';
import { useCanvas } from '../../context/canvasContext';
import { emitUndo, emitRedo } from '../../socket';
import { isOffline } from '../../utils/helper';
import { UndoIcon, RedoIcon } from './icons';

// The span is the tooltip's target: antd strips the class off a disabled
// button it wraps directly, which would drop it back to the native look.
const PodButton = ({ title, onClick, disabled, children }) => (
    <Tooltip title={title} placement="right">
        <span className={styles.target}>
            <button type="button" className={styles.button} onClick={onClick} disabled={disabled} aria-label={title}>
                {children}
            </button>
        </span>
    </Tooltip>
);

/**
 * Undo / redo over the canvas history. `shortcuts` binds Ctrl+Z / Ctrl+Y;
 * only one mounted copy should do so.
 */
export const UndoRedoButtons = ({ shortcuts = false }) => {
    const { id } = useParams();
    const { canvasRef, undoStack, redoStack, reloadStack } = useCanvas();

    const load = (state) => canvasRef.current.loadFromJSON(state, () => canvasRef.current.renderAll());

    const undo = () => {
        if (undoStack.length === 0) return;
        redoStack.push(undoStack.pop());
        const previous = undoStack[undoStack.length - 1];
        if (previous) load(previous);
        else if (reloadStack?.length) load(reloadStack[0]);
        else canvasRef.current.clear();
        !isOffline() && emitUndo(id);
    };

    const redo = () => {
        if (redoStack.length === 0) return;
        const next = redoStack.pop();
        if (next) {
            undoStack.push(next);
            load(next);
        }
        !isOffline() && emitRedo(id);
    };

    useEffect(() => {
        if (!shortcuts) return undefined;
        const onKeyDown = (event) => {
            if (!event.ctrlKey) return;
            if (event.key === 'z' || event.key === 'Z') undo();
            else if (event.key === 'y' || event.key === 'Y') redo();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    });

    return (
        <>
            <PodButton title="Undo" onClick={undo} disabled={undoStack.length === 0}><UndoIcon /></PodButton>
            <PodButton title="Redo" onClick={redo} disabled={redoStack.length === 0}><RedoIcon /></PodButton>
        </>
    );
};

export default UndoRedoButtons;
